import { useCallback, useEffect, useRef, useState } from 'react'
import type { TFunction } from 'i18next'
import { isEmptyTestDataCount, type TestDataActions, type TestDataCount, type TestDataCreated } from '@/src/renderer/lib/testData'

export type TestDataAction = 'create' | 'remove'

/** Was die Rückfrage gerade fragt: Anlegen, oder Entfernen mit den vorher gezählten Zahlen. */
export type TestDataRequest = { action: 'create' } | { action: 'remove'; count: TestDataCount }

export type TestDataOutcome =
  | { kind: 'created'; created: TestDataCreated }
  | { kind: 'removed'; removed: TestDataCount }
  | { kind: 'none' }
  | { kind: 'failed'; action: TestDataAction }

/** „22 Projekte“, „537 Aufgaben“ — für Rückfrage, Toast und Statuszeile. */
export function testDataCountTexts(t: TFunction, count: { projects: number; tasks: number }) {
  return { projects: t('testData.projects', { count: count.projects }), tasks: t('testData.tasks', { count: count.tasks }) }
}

/**
 * **Ablauf von „Testdaten anlegen / entfernen“** (#269): `ask` öffnet die Rückfrage (Entfernen zählt
 * vorher; ist nichts da, gleich `none`), `confirm` führt aus. Das Ergebnis steht in `outcome` und geht an
 * `onOutcome` — die Einstellungen zeigen es als Statuszeile, das Hauptfenster als Toast.
 *
 * @example
 * const flow = useTestDataFlow(useTestDataActions(), { onOutcome: showToast })
 * <TestDataConfirmDialog {...flow.dialog} />
 */
export function useTestDataFlow(actions: TestDataActions | null, { onOutcome }: { onOutcome?: (outcome: TestDataOutcome) => void } = {}) {
  const [request, setRequest] = useState<TestDataRequest | null>(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<TestDataAction | null>(null)
  const [outcome, setOutcome] = useState<TestDataOutcome | null>(null)
  // Doppelklick oder Menü während eines Laufs: nichts zweimal starten.
  const running = useRef(false)
  const onOutcomeRef = useRef(onOutcome)
  onOutcomeRef.current = onOutcome

  // Das Fenster bleibt offen, solange ein Lauf läuft: `deleteTask` meldet die Löschung erst am Ende an den Sync —
  // bräche die Schleife beim Schließen dazwischen ab, bliebe die Aufgabe auf den anderen Geräten stehen. Electron
  // bricht das Schließen still ab, der Browser fragt nach.
  useEffect(() => {
    if (!busy) return
    const hold = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = true
    }
    window.addEventListener('beforeunload', hold)
    return () => window.removeEventListener('beforeunload', hold)
  }, [busy])

  const finish = useCallback((next: TestDataOutcome) => {
    setOutcome(next)
    onOutcomeRef.current?.(next)
  }, [])

  const ask = useCallback(async (action: TestDataAction) => {
    if (!actions || running.current) return
    if (action === 'create') {
      setRequest({ action })
      setOpen(true)
      return
    }
    try {
      const count = await actions.count()
      if (isEmptyTestDataCount(count)) return finish({ kind: 'none' })
      setRequest({ action, count })
      setOpen(true)
    } catch (error) {
      console.error('[testData] count failed', error)
      finish({ kind: 'failed', action })
    }
  }, [actions, finish])

  const confirm = useCallback(async () => {
    if (!actions || !request || running.current) return
    running.current = true
    setOpen(false)
    setBusy(request.action)
    try {
      if (request.action === 'create') finish({ kind: 'created', created: await actions.create() })
      else finish({ kind: 'removed', removed: await actions.remove() })
    } catch (error) {
      console.error(`[testData] ${request.action} failed`, error)
      finish({ kind: 'failed', action: request.action })
    } finally {
      running.current = false
      setBusy(null)
    }
  }, [actions, request, finish])

  return {
    ask,
    busy,
    outcome,
    // `request` bleibt beim Schließen stehen, damit der Text während der Ausblendung nicht wechselt.
    dialog: { open, request, onOpenChange: setOpen, onConfirm: confirm },
  }
}
